<template>
  <div class="home-reporting-kpis px-2 pb-2 w-full">
    <b-card class="mb-0 shadow-none border home-reporting-kpis__card">
      <template #header>
        <div class="d-flex flex-wrap align-items-center justify-content-between w-100 gap-2">
          <div class="d-flex align-items-center flex-wrap">
            <feather-icon icon="BarChartIcon" size="20" class="mr-50 text-primary" />
            <span class="font-weight-bold">Formulário de Relato</span>
            <b-badge variant="light-secondary" class="ml-1">Últimos 15 dias</b-badge>
          </div>
          <b-button
            v-if="canViewReportingDashboardData && $acl.hasPermission('reporting_channel_handling.dashboard.menu')"
            variant="flat-primary"
            size="sm"
            :to="{ name: 'reporting_channel_dashboard_index' }"
          >
            Dashboard completo
            <feather-icon icon="ChevronRightIcon" size="14" class="ml-25" />
          </b-button>
        </div>
      </template>
      <b-card-body v-if="canViewReportingDashboardData" class="pt-2">
        <div v-if="loading" class="text-center py-3 text-muted">
          Carregando indicadores…
        </div>
        <template v-else>
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
              <small class="text-muted mb-0">
                Duas visões disponíveis; a última escolha fica salva neste navegador.
              </small>
              <b-button-group size="sm" class="home-reporting-kpis__toggle">
                <b-button
                  :variant="viewMode === 'general' ? 'primary' : 'outline-primary'"
                  @click="setViewMode('general')"
                >
                  Visão geral
                </b-button>
                <b-button
                  :variant="viewMode === 'all' ? 'primary' : 'outline-primary'"
                  @click="setViewMode('all')"
                >
                  Visão por indicadores
                </b-button>
              </b-button-group>
            </div>

            <b-alert v-if="!hasAnyData" show variant="light" class="mb-0 text-center">
              Não há dados no período para exibir.
            </b-alert>

            <!-- Visão geral: total Situação (tratamento) + donut por situação -->
            <div v-else-if="viewMode === 'general'" class="home-reporting-kpis--general">
              <b-row class="align-items-center">
                <b-col cols="12" md="4" class="text-center text-md-left py-3 py-md-0">
                  <div class="display-4 text-primary font-weight-bold mb-0">
                    {{ totalStatus }}
                  </div>
                  <div class="text-muted small mt-1">
                    Situação (tratamento) no período
                  </div>
                  <div class="text-muted small mt-2 d-none d-md-block">
                    Distribuição por <strong>Situação (tratamento)</strong>.
                  </div>
                </b-col>
                <b-col cols="12" md="8">
                  <template v-if="statusChartSeries.length">
                    <vue-apex-charts
                      type="donut"
                      height="300"
                      :options="statusHomeOptions"
                      :series="statusChartSeries"
                    />
                  </template>
                  <b-alert v-else show variant="info" class="mb-0 small">
                    Não há situação de tratamento classificada para montar o gráfico.
                  </b-alert>
                </b-col>
              </b-row>
            </div>

            <!-- Visão por indicadores: um donut por dimensão -->
            <div v-else class="home-reporting-kpis--all">
              <b-row>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="small font-weight-bold text-uppercase text-muted mb-1">
                    Nível de criticidade
                  </div>
                  <template v-if="chartSeries.length">
                    <vue-apex-charts
                      type="donut"
                      height="240"
                      :options="severityHomeOptions"
                      :series="chartSeries"
                    />
                  </template>
                  <b-alert v-else show variant="light" class="mb-0 py-2 small">Sem dados</b-alert>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="small font-weight-bold text-uppercase text-muted mb-1">
                    Situação (tratamento)
                  </div>
                  <template v-if="statusChartSeries.length">
                    <vue-apex-charts
                      type="donut"
                      height="240"
                      :options="statusHomeOptions"
                      :series="statusChartSeries"
                    />
                  </template>
                  <b-alert v-else show variant="light" class="mb-0 py-2 small">Sem dados</b-alert>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="small font-weight-bold text-uppercase text-muted mb-1">
                    Tipo de relato
                  </div>
                  <template v-if="typeReportingChartSeries.length">
                    <vue-apex-charts
                      type="donut"
                      height="240"
                      :options="typeReportingHomeOptions"
                      :series="typeReportingChartSeries"
                    />
                  </template>
                  <b-alert v-else show variant="light" class="mb-0 py-2 small">Sem dados</b-alert>
                </b-col>
                <b-col cols="12" md="6" class="mb-3">
                  <div class="small font-weight-bold text-uppercase text-muted mb-1">
                    Identificação
                  </div>
                  <template v-if="identificationChartSeries.length">
                    <vue-apex-charts
                      type="donut"
                      height="240"
                      :options="identificationHomeOptions"
                      :series="identificationChartSeries"
                    />
                  </template>
                  <b-alert v-else show variant="light" class="mb-0 py-2 small">Sem dados</b-alert>
                </b-col>
              </b-row>
            </div>
        </template>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BBadge,
  BButton,
  BButtonGroup,
  BAlert
} from 'bootstrap-vue'
import ReportingChannelFormHandlingService from '@/services/api/reporting-channel/ReportingChannelFormHandlingService'
import reportingChannelDashboardChartMixin from '@/views/apps/reporting-channel/mixins/reportingChannelDashboardChartMixin.js'

const VIEW_STORAGE_KEY = 'home_reporting_kpis_view_mode'

export default {
  name: 'HomeReportingChannelKpisCard',
  mixins: [reportingChannelDashboardChartMixin],
  components: {
    VueApexCharts,
    BCard,
    BCardBody,
    BRow,
    BCol,
    BBadge,
    BButton,
    BButtonGroup,
    BAlert
  },
  data() {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - 15)
    let viewMode = 'general'
    try {
      const saved = localStorage.getItem(VIEW_STORAGE_KEY)
      if (saved === 'all' || saved === 'general') {
        viewMode = saved
      }
    } catch (e) {
      /* ignore */
    }
    return {
      loading: true,
      dashboardData: null,
      dateStart: startDate.toISOString().split('T')[0],
      dateEnd: endDate.toISOString().split('T')[0],
      viewMode
    }
  },
  computed: {
    canViewReportingDashboardData() {
      return (
        this.$acl &&
        this.$acl.hasPermission('reporting_channel_handling.dashboard.show')
      )
    },
    severityHomeOptions() {
      if (!this.chartSeries.length) {
        return {}
      }
      return this.homeCompactDonutOptions({
        series: this.chartSeries,
        labels: this.chartLabels,
        colors: this.chartColors,
        total: this.totalFormularios,
        centerLabel: 'Formulários'
      })
    },
    statusHomeOptions() {
      if (!this.statusChartSeries.length) {
        return {}
      }
      return this.homeCompactDonutOptions({
        series: this.statusChartSeries,
        labels: this.statusChartLabels,
        colors: this.statusChartColors,
        total: this.totalStatus,
        centerLabel: 'Total'
      })
    },
    typeReportingHomeOptions() {
      if (!this.typeReportingChartSeries.length) {
        return {}
      }
      return this.homeCompactDonutOptions({
        series: this.typeReportingChartSeries,
        labels: this.typeReportingChartLabels,
        colors: this.typeReportingChartColors,
        total: this.totalTypeReporting,
        centerLabel: 'Total'
      })
    },
    identificationHomeOptions() {
      if (!this.identificationChartSeries.length) {
        return {}
      }
      return this.homeCompactDonutOptions({
        series: this.identificationChartSeries,
        labels: this.identificationChartLabels,
        colors: this.identificationChartColors,
        total: this.totalIdentification,
        centerLabel: 'Total'
      })
    },
    hasAnyData() {
      return (
        this.totalFormularios > 0 ||
        this.totalStatus > 0 ||
        this.totalTypeReporting > 0 ||
        this.totalIdentification > 0
      )
    }
  },
  mounted() {
    if (this.canViewReportingDashboardData) {
      this.loadDashboardData()
    } else {
      this.loading = false
    }
  },
  methods: {
    setViewMode(mode) {
      this.viewMode = mode
      try {
        localStorage.setItem(VIEW_STORAGE_KEY, mode)
      } catch (e) {
        /* ignore */
      }
    },
    loadDashboardData() {
      this.loading = true
      const service = ReportingChannelFormHandlingService.build(this.$vs)
      const params = {
        date_start: this.dateStart,
        date_end: this.dateEnd
      }
      service
        .getDashboardData(params)
        .then(data => {
          this.dashboardData = data
        })
        .catch(() => {
          this.notifyError(
            this.$vs,
            'Não foi possível carregar os indicadores do Formulário de Relato.'
          )
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.home-reporting-kpis__card {
  border-radius: 0.5rem;
}

.home-reporting-kpis__toggle {
  flex-shrink: 0;
}
</style>
